import React, { useMemo } from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  format,
  differenceInDays,
  max,
  min
} from 'date-fns';
import { cn } from '@/lib/utils';

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MAX_VISIBLE_SINGLE_DAY_EVENTS = 2;
const MULTI_DAY_EVENT_HEIGHT = 20;
const MULTI_DAY_GAP = 2;

/**
 * Check if an event spans multiple days
 */
const isMultiDayEvent = (event) => {
  if (!event.endDate) return false;
  const start = new Date(event.startDate);
  const end = new Date(event.endDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return end.getTime() > start.getTime();
};

/**
 * Get single-day events for a specific day
 */
const getSingleDayEventsForDay = (day, events) => {
  const dayNormalized = new Date(day);
  dayNormalized.setHours(0, 0, 0, 0);

  return events.filter((event) => {
    if (!event.startDate || isNaN(new Date(event.startDate).getTime())) {
      return false;
    }
    if (isMultiDayEvent(event)) return false;

    const eventStart = new Date(event.startDate);
    eventStart.setHours(0, 0, 0, 0);

    return eventStart.getTime() === dayNormalized.getTime();
  });
};

/**
 * Calculate multi-day event positions for a week
 */
const calculateMultiDayEventPositions = (weekDays, events) => {
  const weekStart = weekDays[0];
  const weekEnd = weekDays[weekDays.length - 1];

  const multiDayEvents = events.filter(event => {
    if (!isMultiDayEvent(event)) return false;

    const eventStart = new Date(event.startDate);
    const eventEnd = new Date(event.endDate);
    eventStart.setHours(0, 0, 0, 0);
    eventEnd.setHours(23, 59, 59, 999);

    const weekStartNorm = new Date(weekStart);
    weekStartNorm.setHours(0, 0, 0, 0);
    const weekEndNorm = new Date(weekEnd);
    weekEndNorm.setHours(23, 59, 59, 999);

    return eventStart <= weekEndNorm && eventEnd >= weekStartNorm;
  });

  multiDayEvents.sort((a, b) => {
    const aStart = new Date(a.startDate);
    const bStart = new Date(b.startDate);
    if (aStart.getTime() !== bStart.getTime()) {
      return aStart.getTime() - bStart.getTime();
    }
    const aDuration = differenceInDays(new Date(a.endDate || a.startDate), aStart);
    const bDuration = differenceInDays(new Date(b.endDate || b.startDate), bStart);
    return bDuration - aDuration;
  });

  const lanes = [];
  const eventPositions = [];

  multiDayEvents.forEach(event => {
    const eventStart = max([new Date(event.startDate), weekStart]);
    const eventEnd = min([new Date(event.endDate), weekEnd]);

    let assignedLane = -1;
    for (let laneIndex = 0; laneIndex < lanes.length; laneIndex++) {
      const laneEvents = lanes[laneIndex];
      const hasOverlap = laneEvents.some(existingEvent => {
        const existingStart = max([new Date(existingEvent.startDate), weekStart]);
        const existingEnd = min([new Date(existingEvent.endDate), weekEnd]);
        return eventStart <= existingEnd && eventEnd >= existingStart;
      });

      if (!hasOverlap) {
        assignedLane = laneIndex;
        lanes[laneIndex].push(event);
        break;
      }
    }

    if (assignedLane === -1) {
      assignedLane = lanes.length;
      lanes.push([event]);
    }

    const startDayIndex = weekDays.findIndex(d => isSameDay(d, eventStart));
    const endDayIndex = weekDays.findIndex(d => isSameDay(d, eventEnd));

    const actualEventStart = new Date(event.startDate);
    const actualEventEnd = new Date(event.endDate);
    actualEventStart.setHours(0, 0, 0, 0);
    actualEventEnd.setHours(0, 0, 0, 0);

    const weekStartNorm = new Date(weekStart);
    weekStartNorm.setHours(0, 0, 0, 0);
    const weekEndNorm = new Date(weekEnd);
    weekEndNorm.setHours(0, 0, 0, 0);

    const startsThisWeek = actualEventStart >= weekStartNorm;
    const endsThisWeek = actualEventEnd <= weekEndNorm;

    eventPositions.push({
      event,
      lane: assignedLane,
      startCol: Math.max(0, startDayIndex),
      endCol: Math.min(6, endDayIndex >= 0 ? endDayIndex : 6),
      startsThisWeek,
      endsThisWeek,
      // Show title on first visible slot (startCol position)
      showTitle: true
    });
  });

  return {
    positions: eventPositions,
    laneCount: lanes.length
  };
};

/**
 * MultiDayEvent - A spanning event bar
 */
function MultiDayEvent({ event, startCol, endCol, startsThisWeek, endsThisWeek, showLabel, onClick }) {
  const colSpan = endCol - startCol + 1;
  // Use hexColor if available, otherwise fall back to color
  const bgColor = event.hexColor || event.color || '#3B82F6';

  return (
    <button
      onClick={() => onClick?.(event)}
      className={cn(
        'absolute text-[11px] font-medium px-1.5 truncate transition-opacity hover:opacity-90 text-white',
        startsThisWeek ? 'rounded-l' : '',
        endsThisWeek ? 'rounded-r' : ''
      )}
      style={{
        left: `calc(${(startCol / 7) * 100}% + 2px)`,
        width: `calc(${(colSpan / 7) * 100}% - 4px)`,
        height: `${MULTI_DAY_EVENT_HEIGHT}px`,
        lineHeight: `${MULTI_DAY_EVENT_HEIGHT}px`,
        backgroundColor: bgColor
      }}
    >
      {showLabel ? event.title : ''}
    </button>
  );
}

/**
 * SingleDayEvent - A single day event pill
 */
function SingleDayEvent({ event, onClick }) {
  const bgColor = event.hexColor || event.color || '#3B82F6';

  return (
    <button
      onClick={() => onClick?.(event)}
      className="w-full text-left text-[11px] font-medium px-1.5 rounded truncate transition-opacity hover:opacity-90 text-white"
      style={{
        height: `${MULTI_DAY_EVENT_HEIGHT}px`,
        lineHeight: `${MULTI_DAY_EVENT_HEIGHT}px`,
        backgroundColor: bgColor
      }}
    >
      {event.title}
    </button>
  );
}

/**
 * EventOverflow - Shows "+N more" indicator
 */
function EventOverflow({ count, onClick }) {
  if (count <= 0) return null;

  return (
    <button
      onClick={onClick}
      className="w-full text-left text-[11px] text-gray-500 hover:text-gray-700 px-1.5 transition-colors"
      style={{ height: `${MULTI_DAY_EVENT_HEIGHT}px`, lineHeight: `${MULTI_DAY_EVENT_HEIGHT}px` }}
    >
      +{count} more
    </button>
  );
}

/**
 * CalendarWeekRow - A single week row with integrated multi-day events
 */
function CalendarWeekRow({
  weekDays,
  currentMonth,
  events,
  selectedDate,
  onDateClick,
  onEventClick,
  onOverflowClick
}) {
  const { positions: multiDayPositions, laneCount } = useMemo(
    () => calculateMultiDayEventPositions(weekDays, events),
    [weekDays, events]
  );

  const multiDayHeight = laneCount * (MULTI_DAY_EVENT_HEIGHT + MULTI_DAY_GAP);

  return (
    <div className="grid grid-cols-7">
      {weekDays.map((day, dayIndex) => {
        const isCurrentMonth = isSameMonth(day, currentMonth);
        const isSelected = selectedDate && isSameDay(day, selectedDate);
        const isTodayDate = isToday(day);

        const singleDayEvents = getSingleDayEventsForDay(day, events);
        const visibleSingleDayEvents = singleDayEvents.slice(0, MAX_VISIBLE_SINGLE_DAY_EVENTS);
        const overflowCount = singleDayEvents.length - MAX_VISIBLE_SINGLE_DAY_EVENTS;

        return (
          <div
            key={day.toISOString()}
            className={cn(
              'min-h-[110px] border-b border-r border-gray-200 transition-colors relative',
              !isCurrentMonth && 'bg-gray-50/50',
              isSelected && 'bg-blue-50'
            )}
          >
            {/* Day number */}
            <div className="p-1">
              <button
                onClick={() => onDateClick?.(day)}
                className={cn(
                  'w-7 h-7 flex items-center justify-center text-sm rounded-full transition-colors',
                  !isCurrentMonth && 'text-gray-400',
                  isCurrentMonth && 'text-gray-900 hover:bg-gray-100',
                  isTodayDate && 'bg-blue-600 text-white hover:bg-blue-700',
                  isSelected && !isTodayDate && 'bg-blue-100 text-blue-700'
                )}
              >
                {format(day, 'd')}
              </button>
            </div>

            {/* Multi-day events area - positioned relative to this cell */}
            {laneCount > 0 && dayIndex === 0 && (
              <div
                className="absolute left-0 right-0 pointer-events-none"
                style={{
                  top: '36px',
                  height: `${multiDayHeight}px`,
                  width: '700%' // Span all 7 columns
                }}
              >
                {multiDayPositions.map((pos, idx) => (
                  <div
                    key={`${pos.event.id}-${idx}`}
                    className="absolute left-0 right-0 pointer-events-auto"
                    style={{ top: `${pos.lane * (MULTI_DAY_EVENT_HEIGHT + MULTI_DAY_GAP)}px` }}
                  >
                    <MultiDayEvent
                      event={pos.event}
                      startCol={pos.startCol}
                      endCol={pos.endCol}
                      startsThisWeek={pos.startsThisWeek}
                      endsThisWeek={pos.endsThisWeek}
                      showLabel={true}
                      onClick={onEventClick}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Single-day events - positioned below multi-day area */}
            <div
              className="px-1 space-y-0.5"
              style={{ marginTop: laneCount > 0 ? `${multiDayHeight}px` : '0' }}
            >
              {visibleSingleDayEvents.map((event) => (
                <SingleDayEvent
                  key={event.id}
                  event={event}
                  onClick={onEventClick}
                />
              ))}

              {overflowCount > 0 && (
                <EventOverflow
                  count={overflowCount}
                  onClick={() => onOverflowClick?.(day, singleDayEvents)}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * CalendarGrid - Month view with proper multi-day event spanning
 */
export function CalendarGrid({
  currentDate,
  selectedDate,
  events = [],
  onDateClick,
  onEventClick,
  onOverflowClick,
  weekStartsOnMonday = true,
  className
}) {
  const { weeks } = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: weekStartsOnMonday ? 1 : 0 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: weekStartsOnMonday ? 1 : 0 });

    const allDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    const weeks = [];
    for (let i = 0; i < allDays.length; i += 7) {
      weeks.push(allDays.slice(i, i + 7));
    }

    return { weeks };
  }, [currentDate, weekStartsOnMonday]);

  const dayHeaders = weekStartsOnMonday
    ? DAYS_OF_WEEK
    : ['Sun', ...DAYS_OF_WEEK.slice(0, 6)];

  return (
    <div className={cn('border-t border-l border-gray-200 rounded-lg overflow-hidden', className)}>
      {/* Day headers */}
      <div className="grid grid-cols-7 bg-gray-50">
        {dayHeaders.map((day) => (
          <div
            key={day}
            className="py-2 px-2 text-center text-xs font-medium text-gray-500 border-b border-r border-gray-200 uppercase tracking-wide"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar weeks */}
      <div className="bg-white">
        {weeks.map((weekDays, weekIndex) => (
          <CalendarWeekRow
            key={weekIndex}
            weekDays={weekDays}
            currentMonth={currentDate}
            events={events}
            selectedDate={selectedDate}
            onDateClick={onDateClick}
            onEventClick={onEventClick}
            onOverflowClick={onOverflowClick}
          />
        ))}
      </div>
    </div>
  );
}

export default CalendarGrid;
